import { PermissionLevels, Site } from '@debbie/common/dist/index.js';
import { TenantStatus } from '@debbie/common/dist/tenants/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import { useTenantId } from '@debbie/cortex/dist/tenant/index.js';
import { useUserById } from '@debbie/cortex/dist/users/index.js';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import CreditorOrTenant from './CreditorOrTenant.js';
import { ExternalLink, Inbox, Phone } from './Icons/index.js';
import PlaceholderLoader from './PlaceholderLoader.js';
import TenantStatusBadge from './TenantStatusBadge.js';
import Tooltip from './Tooltip.js';
import UserType from './UserType.js';
const UserPreview = (props) => {
    const { user, style, active, onClick } = props;
    const site = useSelector((s) => s.site);
    const [hovered, setHovered] = useState(false);
    const [showTooltipOnTop, setShowTooltipOnTop] = useState(false);
    const ref = useRef(null);
    const onMouseIn = () => {
        setHovered(true);
    };
    const onMouseOut = () => {
        setHovered(false);
    };
    if (!(user != null)) {
        return null;
    }
    return (React.createElement(UserPreviewStyled, { ref: ref, onMouseEnter: onMouseIn, onMouseLeave: onMouseOut, style: style, onClick: onClick, active: active, user: user },
        React.createElement("div", { className: "user-short" },
            user.firstName && user.firstName.slice(0, 1),
            user.lastName && user.lastName.slice(0, 1)),
        hovered &&
            !(props.noTooltip ?? false) &&
            user.id &&
            site === Site.Caseworker && (React.createElement(Tooltip, { tooltipToLeft: props.tooltipToLeft, parentRef: ref, setShowTooltipOnTop: setShowTooltipOnTop, shouldShowOnTop: showTooltipOnTop, height: 300, style: { width: '350px' } },
            React.createElement(UserPreviewTooltip, { userId: user.id })))));
};
export default UserPreview;
const size = 27;
const getBackgroundColor = (user) => {
    switch (user.role) {
        case PermissionLevels.admin:
        case PermissionLevels.superAdmin:
            return '#D07474';
        case PermissionLevels.client:
            return '#7773E8';
        case PermissionLevels.debtor:
            return '#ED9E38';
        default:
            return '#eec125';
    }
};
const UserPreviewStyled = styled.div `
  width: ${size}px;
  height: ${size}px;
  display: inline-block;
  border-radius: 100%;
  margin-right: 5px;
  background-color: ${(props) => getBackgroundColor(props.user)};
  position: relative;

  & > div.user-short {
    text-align: center;
    line-height: 27px;
    color: ${THEME.COLOR_SECONDARY};
    font-size: 11px;
    width: 100%;
  }

  ${(props) => (props.active ?? false)
    ? `
      &::before {
        content: ' ';
        border: 2px solid ${THEME.COLOR_ACCENT};
        width: 35px;
        height: 35px;
        border-radius: 100%;
        display: inline-block;
        position: absolute;
        left: -4px;
        top: -4px;
      }
    `
    : ''}
`;
const UserPreviewTooltip = (props) => {
    const userLoadable = useUserById(props.userId);
    const user = h.hasData(userLoadable) ? userLoadable.data : null;
    const site = useSelector((s) => s.site);
    const lang = useSelector((s) => s.lang);
    const tenantId = useTenantId();
    const meRole = useRole();
    return h.isLoading(userLoadable) ? (React.createElement(PlaceholderLoader, null)) : user ? (React.createElement("div", null,
        site === Site.Caseworker && (React.createElement("div", null,
            React.createElement("p", { style: {
                    fontSize: '0.7em',
                    fontWeight: 900,
                    margin: '0px',
                    color: THEME.DARK_COLOR_SECONDARY,
                    float: 'right',
                } }, moment(user.createdAt).format(THEME.DATE_FORMAT_LONG)),
            user.details.meetingAccountType === 'meeting_only' ? null : (React.createElement(UserType, { role: user.role })))),
        React.createElement("h3", { style: { marginBottom: '.5em' } },
            user.firstName,
            " ",
            user.lastName,
            ' ',
            site === Site.Caseworker &&
                ['admin', 'caseworker', 'client'].includes(user.role) &&
                meRole === 'admin' && (React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => {
                    c.redirect.redirect(`/settings/users/${user.id}`);
                } }))),
        site === Site.Caseworker && (React.createElement("div", null,
            React.createElement("p", { style: {
                    margin: '0 0 30px 0',
                    fontWeight: '400',
                    fontSize: '0.8em',
                } },
                (user.email ?? '') && (React.createElement("a", { style: { color: 'inherit', textDecoration: 'none' }, href: `mailto:${user.email}` },
                    React.createElement(Inbox, { style: { margin: '0.2em 0 -0.2em 0' } }),
                    " ",
                    user.email)),
                React.createElement("br", null),
                user.phone != null && user.phone.number !== '' ? (React.createElement("a", { style: { color: 'inherit', textDecoration: 'none' }, href: `tel:${user.phone.locale}${user.phone.number}` },
                    React.createElement(Phone, { style: { margin: '0.2em 0 -0.2em 0' } }),
                    ' ',
                    user.phone.locale ? React.createElement("span", null,
                        "+",
                        user.phone.locale,
                        " ") : null,
                    ' ',
                    user.phone.number)) : null))),
        site === Site.Caseworker &&
            // Remove so that customer names are not leaked at demos
            !(user.details.isSystemAgent ?? false) &&
            ['admin', 'caseworker'].includes(user.role) &&
            user.tenants &&
            !!user.tenants.length ? (React.createElement("div", null, user.tenants.map((tenant) => {
            return (React.createElement(CreditorOrTenant, { key: tenant.id, active: tenant.id === tenantId, name: tenant.name, description: React.createElement(React.Fragment, null,
                    React.createElement("b", null, tenant.countryCode),
                    " ",
                    tenant.name), badge: tenant.status !== TenantStatus.Active ? (React.createElement(TenantStatusBadge, { status: tenant.status, style: { float: 'right' } })) : undefined }));
        }))) : null,
        ['client'].includes(user.role) &&
            user.creditors &&
            !!user.creditors.length ? (React.createElement("div", null, user.creditors.map((creditor) => {
            return React.createElement(CreditorPreview, { creditorId: creditor.id });
        }))) : null)) : null;
};
const CreditorPreview = (props) => {
    const creditorLoadable = useCreditor(props.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const selectedCreditorId = useSelector((s) => s.selectedCreditor);
    if (!creditor) {
        return null;
    }
    return (React.createElement(CreditorOrTenant, { key: creditor.id, active: creditor.creditorId === selectedCreditorId, name: creditor.name, description: React.createElement(React.Fragment, null, creditor.name) }));
};
//# sourceMappingURL=UserPreview.js.map